import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Clock } from "lucide-react";
import { api } from "@/lib/api";

const BLUE = "#1E4FFF";

function diffParts(target) {
  const ms = Math.max(0, target - Date.now());
  const totalSec = Math.floor(ms / 1000);
  const days = Math.floor(totalSec / 86400);
  const hours = Math.floor((totalSec % 86400) / 3600);
  const minutes = Math.floor((totalSec % 3600) / 60);
  const seconds = totalSec % 60;
  return { ms, days, hours, minutes, seconds };
}

const pad = (n) => String(n).padStart(2, "0");

/**
 * Sticky announcement bar counting down to the free-trial deadline.
 * Reads /api/trial-window (single source of truth). After the cutoff it flips
 * to a "trials ended — choose a plan" message.
 */
export default function TrialCountdown({ ctaTo = "/signup", ctaLabel = "Start free" }) {
  const [win, setWin] = useState(null); // { cutoff_iso, trial_open }
  const [, tick] = useState(0);

  useEffect(() => {
    api.get("/trial-window").then((r) => setWin(r.data)).catch(() => setWin(null));
  }, []);

  useEffect(() => {
    const id = setInterval(() => tick((n) => n + 1), 1000);
    return () => clearInterval(id);
  }, []);

  if (!win) return null;

  const cutoff = new Date(win.cutoff_iso).getTime();
  const { ms, days, hours, minutes, seconds } = diffParts(cutoff);
  const open = win.trial_open && ms > 0;

  if (!open) {
    return (
      <div
        className="w-full text-white"
        style={{ background: "linear-gradient(90deg,#0B0B12,#1a1a24)" }}
        data-testid="trial-countdown-closed"
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-11 flex items-center justify-center gap-3 text-center">
          <span className="font-label text-xs sm:text-sm tracking-wide">
            FREE TRIALS HAVE ENDED — choose a plan to get started
          </span>
          <Link to="/#pricing" data-testid="trial-countdown-cta-closed">
            <span className="font-label text-xs sm:text-sm px-3 py-1 rounded-full bg-white text-[#0B0B12] whitespace-nowrap">
              See plans
            </span>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full text-white" style={{ backgroundColor: BLUE }} data-testid="trial-countdown-open">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 min-h-11 py-1.5 flex flex-wrap items-center justify-center gap-x-3 gap-y-1 text-center">
        <span className="flex items-center gap-1.5 font-label text-xs sm:text-sm tracking-wide">
          <Clock className="w-4 h-4" />
          30-DAY FREE TRIAL
        </span>
        <Link to={ctaTo} data-testid="trial-countdown-cta-open">
          <span className="font-label text-xs sm:text-sm px-3 py-1 rounded-full bg-white whitespace-nowrap" style={{ color: BLUE }}>
            {ctaLabel} →
          </span>
        </Link>
      </div>
    </div>
  );
}
